import {
  Overlay,
  OverlayConfig,
  OverlayModule,
  OverlayRef,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  SelectOptionData,
  SelectOrientationEnum,
  SelectVariationEnum,
} from '@mzic/mzic-interfaces';
import { MzicSvgComponent } from '../mzic-svg/mzic-svg.component';
import { SelectSearchComponent } from '../select-search/select-search.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { BoldCurrencyPipe } from './pipes/bold-currency/bold-currency.pipe';
import { ExtractTitlePipe } from './pipes/extract-title/extract-title.pipe';

@Component({
  selector: 'mzic-select',
  standalone: true,
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  imports: [
    CommonModule,
    MzicSvgComponent,
    SelectSearchComponent,
    BoldCurrencyPipe,
    ExtractTitlePipe,
    SpinnerComponent,
    OverlayModule,
  ],
})
export class SelectComponent implements OnChanges, OnDestroy {
  @Input() variation: SelectVariationEnum = SelectVariationEnum.Default;
  @Input() orientation: SelectOrientationEnum | undefined;
  @Input() optionsSelected: SelectOptionData[] | undefined;
  @Input() optionsLabel: string | undefined;
  @Input() placeholder = '';
  @Input() useLightStyle = false;
  @Input() useSuccessStyle = false;
  @Input() useErrorStyle = false;
  @Input() disabled = false;
  @Input() editabble = true;
  @Input() searchable = false;
  @Input() extraPadding = false;
  @Input() showSearchLoading = false;
  @Input() invalid = false;
  @Input() multiple = false;
  @Input() loading = false;
  @Input() imageShape: 'square' | 'circle' = 'circle';
  @Input() clearable = false;

  @Output() searchTextChange = new EventEmitter<string>();
  @Output() optionChange = new EventEmitter<(string | number)[] | undefined>();
  @Output() closedChange = new EventEmitter<boolean>();
  @Output() selectionCleared = new EventEmitter<void>();

  get hasOptionsSelected() {
    return this.optionsSelected && this.optionsSelected?.length > 0;
  }

  get optionSelected() {
    const [option] = this.optionsSelected ?? [];
    return option;
  }

  get hasDescription(): boolean {
    return this.optionSelected?.description != null;
  }

  // Controla a exibição dos resultados
  opened = false;

  // Garante que ao clicar no botão de limpar a pesquisa, o conteúdo se mantenha aberto
  searchCleaned = false;

  selectVariationEnum = SelectVariationEnum;
  selectOrientationEnum = SelectOrientationEnum;

  @ViewChild('dropdownContent') dropdownTpl!: TemplateRef<any>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.opened) {
      return;
    }

    const target = event.target as HTMLElement;
    const clickedInsideSelect = target.closest('.select');
    const clickedInsideOverlay = target.closest('.cdk-overlay-pane');
    const clickedInsideSelectOverlay = target.closest('.select-overlay');

    const clickedInside =
      clickedInsideSelect || clickedInsideOverlay || clickedInsideSelectOverlay;

    // Check if the clicked element is this specific select instance
    const isThisSelect =
      clickedInsideSelect &&
      this.elementRef.nativeElement.contains(clickedInsideSelect);

    if (!clickedInside && !this.searchCleaned) {
      this.closeDropdown();
    } else if (clickedInsideSelect && !isThisSelect) {
      // Clicked on a different select, close this one
      this.closeDropdown();
    }

    if (this.searchCleaned) {
      this.searchCleaned = false;
    }
  }

  // Acessibilidade, se apertar ESC, o select deve fechar
  @HostListener('document:keydown.escape', ['$event'])
  handleEscape() {
    if (this.opened) {
      this.closeDropdown();
    }
  }

  private overlayRef: OverlayRef | null = null;

  constructor(
    public elementRef: ElementRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnDestroy(): void {
    this.closeOverlay();
  }

  private createOverlay() {
    if (this.overlayRef) {
      return;
    }

    /**
     * withPositions = Define as posições que o overlay deve ser posicionado, o primeiro é o posicionamento padrão
     */
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -4,
        },
      ]);

    /**
     * hasBackdrop garante que não vai bugar com o scroll da página
     */
    const overlayConfig = new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      width: this.elementRef.nativeElement.offsetWidth,
      panelClass: ['select-overlay'],
    });

    this.overlayRef = this.overlay.create(overlayConfig);

    // Listen for backdrop clicks to close dropdown
    this.overlayRef.backdropClick().subscribe(() => {
      this.closeDropdown();
    });

    // Anexa o conteúdo do template ao overlay
    const portal = new TemplatePortal(this.dropdownTpl, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  protected closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef = null;
    }
  }

  protected closeDropdown() {
    this.opened = false;
    this.closeOverlay();
    this.closedChange.emit(true);
  }

  toggleContent() {
    if (this.loading) {
      return;
    }

    // Caso o select esteja desabilitado e não é editável, não abrir
    if (this.disabled || !this.editabble) {
      return;
    }

    // If clearable and has selection, only prevent opening (not closing)
    if (this.clearable && this.hasOptionsSelected && !this.opened) {
      return;
    }

    this.opened = !this.opened;

    if (this.opened) {
      this.createOverlay();
    } else {
      this.closeDropdown();
    }
  }

  handleClearTextChange(cleaned: boolean) {
    this.searchCleaned = cleaned;
  }

  handleSearchTextChange(event: string) {
    this.searchTextChange.emit(event);
  }

  clearSelection(event?: Event) {
    if (event) {
      event.stopPropagation(); // Prevent dropdown from opening
    }
    this.optionsSelected = undefined;
    this.selectionCleared.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.loading) {
      return;
    }

    // Only auto-open if there's a specific user interaction scenario
    // Avoid auto-opening during normal component initialization or data updates
    if (!changes['optionsSelected']?.firstChange && !this.multiple && this.opened) {
      // Only check for auto-toggle if the dropdown is already open
      // This prevents unwanted opening during initialization
      
      // Casos especiais que ele bloqueia a exibição dos itens
      if (!changes['optionsSelected']) {
        if (
          changes['orientation'] ||
          changes['disabled'] ||
          changes['placeholder'] ||
          changes['useSuccessStyle'] ||
          changes['useErrorStyle'] ||
          changes['extraPadding'] ||
          changes['loading'] ||
          changes['imageShape'] ||
          changes['invalid']?.currentValue
        ) {
          return;
        }
      }

      this.toggleContent();
    }
  }
}
